import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlay } = FiIcons;

const videos = [
  {
    url: "https://ik.imagekit.io/aeu02xvjgn/Midsummer/HRNDZ3271_8.mp4?updatedAt=1782321526456",
    category: "COURTYARD MOMENTS",
    title: "Music Beneath the Trees",
    featured: true,
    mobile: true
  },
  {
    url: "https://ik.imagekit.io/aeu02xvjgn/Midsummer/HRNDZ3271_24.mp4?updatedAt=1782321521051",
    category: "THE EXPERIENCE",
    title: "The Details Couples Remember",
    featured: false,
    mobile: true
  },
  {
    url: "https://ik.imagekit.io/aeu02xvjgn/Midsummer/HRNDZ3271.mp4?updatedAt=1782321519119",
    category: "LIVE MUSIC",
    title: "Sound and Celebration",
    featured: false,
    mobile: true
  },
  {
    url: "https://ik.imagekit.io/aeu02xvjgn/Midsummer/HRNDZ3271_23.mp4?updatedAt=1782321518512",
    category: "ESTATE ENERGY",
    title: "An Open House, Reimagined",
    featured: false,
    mobile: true
  },
  {
    url: "https://ik.imagekit.io/aeu02xvjgn/Midsummer/HRNDZ3271_22.mp4?updatedAt=1782321506255",
    category: "ESTATE GARDENS",
    title: "The Grounds",
    featured: false,
    mobile: true
  },
  {
    url: "https://ik.imagekit.io/aeu02xvjgn/Midsummer/HRNDZ3271_45.mp4?updatedAt=1782321540883",
    category: "PADUA ATMOSPHERE",
    title: "A Midsummer Afternoon at Padua",
    featured: false,
    mobile: false,
    desktopWide: true
  }
];

function VideoCard({ video, index }) {
  const [isHovered, setIsHovered] = useState(false);
  const [hasError, setHasError] = useState(false);

  if (hasError) return null;

  const getCardClasses = () => {
    let classes = "relative group overflow-hidden rounded-[18px] md:rounded-[24px] bg-charcoal/10 border-[0.5px] border-estate-gold/20 transition-all duration-700 w-full ";
    classes += "aspect-[4/5] min-h-[420px] ";

    if (video.featured) {
      classes += "md:col-span-2 md:row-span-2 md:aspect-auto md:min-h-0";
    } else if (video.desktopWide) {
      classes += "md:col-span-3 md:h-[400px] md:aspect-auto md:min-h-0";
    } else {
      classes += "md:h-[320px] md:aspect-auto md:min-h-0";
    }

    if (!video.mobile) {
      classes += " hidden md:block";
    }

    return classes;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.8 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={getCardClasses()}
    >
      <video
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        src={video.url}
        onError={() => setHasError(true)}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2.5s] ease-out group-hover:scale-110"
      />

      <div className="absolute inset-0 bg-gradient-to-t from-burgundy/80 via-transparent to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-700"></div>

      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <motion.div 
          animate={{ 
            opacity: isHovered ? 0.9 : 0.4,
            scale: isHovered ? 1.1 : 1
          }}
          className="w-12 h-12 md:w-16 md:h-16 rounded-full border border-ivory/40 flex items-center justify-center backdrop-blur-sm"
        >
          <SafeIcon icon={FiPlay} className="text-ivory text-base md:text-xl ml-0.5" />
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 w-full p-6 md:p-8 z-10">
        <motion.span 
          animate={{ y: isHovered ? 0 : 3 }}
          className="font-sans text-[8px] md:text-[10px] uppercase tracking-[0.4em] text-estate-gold block mb-2"
        >
          {video.category}
        </motion.span>
        <motion.h3 
          animate={{ y: isHovered ? 0 : 3 }}
          className={`font-serif text-ivory leading-tight transition-all duration-500 ${
            video.featured ? 'text-2xl md:text-4xl' : 'text-lg md:text-2xl'
          }`}
        >
          {video.title}
        </motion.h3>
      </div>
    </motion.div>
  );
}

export default function CinematicGallery() {
  return (
    <section className="bg-aged-ivory pt-12 pb-10 md:py-32 px-6 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-24 max-w-3xl mx-auto"
        >
          <span className="font-sans text-[10px] md:text-xs uppercase tracking-[0.5em] text-estate-gold block mb-4 md:mb-6">
            Estate Atmosphere
          </span>
          <h2 className="font-serif text-3xl md:text-6xl text-burgundy mb-4 md:mb-8">
            Cinematic Moments
          </h2>
          <p className="font-sans font-light text-charcoal/70 text-sm md:text-base tracking-wide leading-relaxed px-4">
            A glimpse of the music, movement, courtyard energy, and Spanish Revival atmosphere 
            couples can experience at The Padua Midsummer Affair.
          </p>
        </motion.div>

        <div className="flex flex-col gap-5 md:grid md:grid-cols-3 md:gap-8">
          {videos.filter(v => !v.desktopWide).map((video, idx) => (
            <VideoCard key={idx} video={video} index={idx} />
          ))}
          {videos.filter(v => v.desktopWide).map((video, idx) => (
            <VideoCard key="wide" video={video} index={5} />
          ))}
        </div>
      </div>
    </section>
  );
}